import { useState, useEffect } from 'react';
import { authService } from '@/lib/auth/authService';
import { Lock, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AuthModal from '@/components/auth/AuthModal';

interface ProductPriceDisplayProps {
  regularPrice: number;
  discountPrice?: number;
  currencyCode?: string;
  product?: {
    name: string;
    id: string;
  };
}

export default function ProductPriceDisplay({
  regularPrice,
  discountPrice,
  currencyCode = 'EUR',
  product
}: ProductPriceDisplayProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [specialPrice, setSpecialPrice] = useState<number | null>(null);

  // Comprobar si el usuario está autenticado
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);

      // Si está autenticado, calcular un precio especial (simulado)
      if (authenticated) {
        // Aquí se podría consultar una API para obtener el precio real para el usuario
        // Por ahora simulamos un descuento adicional del 10% sobre el precio regular o de descuento
        const basePrice = discountPrice || regularPrice;
        const userSpecialPrice = basePrice * 0.9; // 10% de descuento adicional
        setSpecialPrice(userSpecialPrice);
      }
    };

    checkAuth();
  }, [regularPrice, discountPrice]);

  // Formatear precio como EUR
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(price);
  };

  // Calcular el porcentaje de descuento
  const calculateDiscountPercentage = (original: number, discounted: number) => {
    const percentage = ((original - discounted) / original) * 100;
    return Math.round(percentage);
  };

  const handleAuthSuccess = () => {
    setIsAuthModalOpen(false);
    setIsAuthenticated(true);

    // Calcular precio especial después de autenticación exitosa
    const basePrice = discountPrice || regularPrice;
    const userSpecialPrice = basePrice * 0.9;
    setSpecialPrice(userSpecialPrice);
  };

  // Mostrar diferentes opciones según el estado de autenticación
  if (isAuthenticated && specialPrice !== null) {
    return (
      <div className="space-y-2">
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold text-white">
            {formatPrice(specialPrice)}
          </span>
          {(discountPrice || regularPrice !== specialPrice) && (
            <span className="text-lg text-zinc-500 line-through">
              {formatPrice(regularPrice)}
            </span>
          )}
          {regularPrice !== specialPrice && (
            <span className="text-sm px-2 py-0.5 bg-green-900/30 text-green-400 rounded-full">
              -{calculateDiscountPercentage(regularPrice, specialPrice)}%
            </span>
          )}
        </div>
        <div className="text-xs text-[#FCAA0D] font-medium">
          Precio exclusivo para usuarios registrados
        </div>
        <div className="mt-4">
          <Button className="w-full gap-2 bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-bold">
            <ShoppingCart className="w-4 h-4" />
            Solicitar presupuesto
          </Button>
        </div>
      </div>
    );
  }

  // Usuario no autenticado
  return (
    <div className="space-y-2">
      {discountPrice ? (
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold text-white">
            {formatPrice(discountPrice)}
          </span>
          <span className="text-lg text-zinc-500 line-through">
            {formatPrice(regularPrice)}
          </span>
          <span className="text-sm px-2 py-0.5 bg-green-900/30 text-green-400 rounded-full">
            -{calculateDiscountPercentage(regularPrice, discountPrice)}%
          </span>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold text-white">
            {formatPrice(regularPrice)}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-4">
        <Button
          variant="outline"
          className="w-full border-dashed border-[#FCAA0D]/40 gap-2 hover:bg-[#FCAA0D]/10"
          onClick={() => setIsAuthModalOpen(true)}
        >
          <Lock className="w-4 h-4 text-[#FCAA0D]" />
          Ver precio exclusivo
        </Button>

        <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white">
          Solicitar información
        </Button>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccessfulAuth={handleAuthSuccess}
      />
    </div>
  );
}
